/**
 * Release Notes Service
 *
 * Handles fetching changelog content and tracking user's last seen version.
 */

import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { nuclearn_api_url, nuclearn_version } from "../../App";

// Types
export interface ReleaseNotesStatus {
  has_unseen_release: boolean;
  last_seen_version: string | null;
  current_version: string;
  auto_popup_enabled: boolean;
}

export interface MarkSeenResponse {
  success: boolean;
  last_seen_version: string;
}

export interface ResetAllResponse {
  success: boolean;
  users_reset: number;
}

export interface ReleaseNotesConfig {
  auto_popup_enabled: boolean;
}

// API functions
const getReleaseNotesStatus = async (): Promise<ReleaseNotesStatus> => {
  const response = await axios.get(`${nuclearn_api_url}/release-notes/status`, {
    params: { current_version: nuclearn_version },
  });
  return response.data;
};

const markReleaseNotesSeen = async (
  version: string,
): Promise<MarkSeenResponse> => {
  const response = await axios.post(
    `${nuclearn_api_url}/release-notes/mark-seen`,
    { version },
  );
  return response.data;
};

const fetchChangelog = async (): Promise<string> => {
  // Fetch from the public directory - works both in dev and production
  const response = await axios.get("/CHANGELOG.md", {
    baseURL: window.location.origin,
    headers: {
      Accept: "text/plain",
    },
  });
  return response.data;
};

// React Query hooks
export const useGetReleaseNotesStatus = () => {
  return useQuery(["releaseNotesStatus"], getReleaseNotesStatus, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    retry: false, // Don't retry on failure - this is a non-critical feature
  });
};

export const useMarkReleaseNotesSeen = () => {
  const queryClient = useQueryClient();

  return useMutation(markReleaseNotesSeen, {
    onMutate: async () => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries(["releaseNotesStatus"]);

      // Snapshot the previous value for rollback
      const previousStatus = queryClient.getQueryData<ReleaseNotesStatus>([
        "releaseNotesStatus",
      ]);

      // Optimistically update the cache immediately
      if (previousStatus) {
        queryClient.setQueryData<ReleaseNotesStatus>(["releaseNotesStatus"], {
          ...previousStatus,
          has_unseen_release: false,
        });
      }

      return { previousStatus };
    },
    onError: (error: any, _, context: any) => {
      // Rollback to the previous value on error
      if (context?.previousStatus) {
        queryClient.setQueryData(
          ["releaseNotesStatus"],
          context.previousStatus,
        );
      }
      console.error("Error marking release notes as seen:", error.response);
    },
    onSettled: () => {
      // Refetch after mutation settles to ensure consistency
      queryClient.invalidateQueries(["releaseNotesStatus"]);
    },
  });
};

export const useFetchChangelog = () => {
  return useQuery(["changelog"], fetchChangelog, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 30, // 30 minutes - changelog doesn't change often
    cacheTime: 1000 * 60 * 60, // 1 hour
    retry: 1,
  });
};

// Admin function to reset release notes for all users
const resetReleaseNotesForAll = async (): Promise<ResetAllResponse> => {
  const response = await axios.post(
    `${nuclearn_api_url}/release-notes/reset-all`,
  );
  return response.data;
};

export const useResetReleaseNotesForAll = () => {
  const queryClient = useQueryClient();

  return useMutation(resetReleaseNotesForAll, {
    onSuccess: () => {
      // Invalidate the status query
      queryClient.invalidateQueries(["releaseNotesStatus"]);
    },
  });
};

// Admin function to get release notes config
const getReleaseNotesConfig = async (): Promise<ReleaseNotesConfig> => {
  const response = await axios.get(`${nuclearn_api_url}/release-notes/config`);
  return response.data;
};

export const useGetReleaseNotesConfig = () => {
  return useQuery(["releaseNotesConfig"], getReleaseNotesConfig, {
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
};

// Admin function to update release notes config
const updateReleaseNotesConfig = async (
  config: ReleaseNotesConfig,
): Promise<ReleaseNotesConfig> => {
  const response = await axios.put(
    `${nuclearn_api_url}/release-notes/config`,
    config,
  );
  return response.data;
};

export const useUpdateReleaseNotesConfig = () => {
  const queryClient = useQueryClient();

  return useMutation(updateReleaseNotesConfig, {
    onSuccess: () => {
      queryClient.invalidateQueries(["releaseNotesConfig"]);
      queryClient.invalidateQueries(["releaseNotesStatus"]);
    },
  });
};

// Helper to get current version
export const getCurrentVersion = (): string => {
  return nuclearn_version || "0.0.0";
};
